import React, { useEffect, useState, useCallback, useRef } from "react";
import { ReactComponent as RejectedIcon } from "../../../Assets/Icons/rejectedIcon.svg";
import { ReactComponent as ApprovedIcon } from "../../../Assets/Icons/approvedIcon.svg";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// Use the project's original loader for consistency
import Loader from "../Loader";
import { getNotificationsByUser, markNotificationAsRead, markAllNotificationsAsRead } from "../../../services/notificationService";
import { updateUnreadCount } from "../../../redux/slices/notificationSlice";
import { MERCHANT_NOTIFICATION_TYPES, FUNDRAISER_NOTIFICATION_TYPES, formatNotificationMessage } from "../../../utils/notificationTypes";
import moment from "moment";

// Custom hook for notification state management
const useNotificationState = (role) => {
  const [notifications, setNotifications] = useState([]);
  const [lastPollTime, setLastPollTime] = useState(Date.now());

  const updateNotification = useCallback((notificationId, updates) => {
    setNotifications(prev => 
      prev.map(notification => 
        (notification.id === notificationId || notification._id === notificationId)
          ? { ...notification, ...updates }
          : notification
      )
    );
  }, []);

  const updateAllNotifications = useCallback((updates) => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, ...updates }))
    );
  }, []);

  const setNotificationsData = useCallback((data) => {
    if (data && Array.isArray(data)) {
      setNotifications(data);
      setLastPollTime(Date.now());
    } else {
      setNotifications([]);
    }
  }, []);

  return {
    notifications,
    lastPollTime,
    updateNotification,
    updateAllNotifications,
    setNotificationsData
  };
};

const Notifications = ({ isVisible, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Location hook removed as not required for this design
  const loading = useSelector((state) => state?.notificationReducer?.loading);
  const unreadCount = useSelector((state) => state?.notificationReducer?.unreadCount);
  const storeNotifications = useSelector((state) => state?.notificationReducer?.notifications);
  const role = useSelector(
    (state) => state?.authReducer?.tokenData?.user?.role
  );
  // We no longer poll periodically; data loads on open or manual refresh

  // Use custom hook for notification state
  const {
    notifications,
    updateNotification,
    setNotificationsData
  } = useNotificationState(role);

  // Ref to scroll to the viewed section when an item is marked read
  const viewedSectionRef = useRef(null);

  // Animation classes based on visibility
  const slideInClass = isVisible 
    ? "translate-x-0 opacity-100" 
    : "translate-x-full opacity-0";

  const getNotifications = useCallback(async () => {
    try {
      const notificationPromise = await dispatch(
        getNotificationsByUser({ role: role || "merchant" })
      )?.unwrap();
      
      // Debug: Log the response to see the actual data structure
      console.log('Notification response:', notificationPromise);
      
      // Only set notifications if we have real data from backend
      if (notificationPromise?.data && Array.isArray(notificationPromise.data)) {
        const normalizeTimestamp = (n) => {
          const ts = n?.createdAt || n?.created_at || n?.timestamp || n?.time || n?.date;
          const d = ts ? new Date(ts) : null;
          return d && !isNaN(d.getTime()) ? d.getTime() : 0;
        };

        // Sort newest first to ensure latest notifications appear at the top
        const newNotifications = [...notificationPromise.data].sort((a, b) => normalizeTimestamp(b) - normalizeTimestamp(a));
        
        // Preserve local read state across refreshes by merging flags
        const previouslyReadIdSet = new Set(
          (notifications || [])
            .filter(n => n?.isRead)
            .map(n => n.id || n._id || n.notificationId || n.notification_id)
            .filter(Boolean)
        );
        const mergedNotificationsRaw = newNotifications.map(n => {
          const id = n.id || n._id || n.notificationId || n.notification_id;
          const backendRead = (
            n.isRead === true ||
            n.read === true ||
            n.is_read === true ||
            n.seen === true
          );
          const isRead = previouslyReadIdSet.has(id) ? true : backendRead;
          return { ...n, isRead };
        });
        // De-duplicate by id (fallback to createdAt+title hash) to avoid duplicate render in sections
        const dedupMap = new Map();
        for (const n of mergedNotificationsRaw) {
          const key = (n.id || n._id || n.notificationId || n.notification_id) || `${n.createdAt || n.created_at || ''}|${n.title || n.subject || ''}`;
          if (!dedupMap.has(key)) dedupMap.set(key, n);
        }
        const mergedNotifications = Array.from(dedupMap.values());
        
        // Debug: Log the first notification to see its structure
        if (newNotifications.length > 0) {
          console.log('First notification structure:', newNotifications[0]);
        }
        
        setNotificationsData(mergedNotifications);
        // Sync unread counter with merged local state so the dot count and button align
        const localUnread = mergedNotifications.filter(n => !n.isRead).length;
        dispatch(updateUnreadCount(localUnread));
      } else {
        setNotificationsData([]);
        dispatch(updateUnreadCount(0));
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  }, [dispatch, role, setNotificationsData]);

  const handleMarkAsRead = async (notificationId) => {
    // Optimistic update for instant UI feedback
    updateNotification(notificationId, { isRead: true });
    try {
      await dispatch(markNotificationAsRead(notificationId)).unwrap();
    } catch (error) {
      console.error("Error marking notification as read:", error);
      // Keep UI as read even if backend fails; user expectation is immediate blue dot removal
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const result = await dispatch(markAllNotificationsAsRead()).unwrap();
      if (result?.success) {
        // Optimistically reflect read state
        setNotificationsData(
          (notifications || []).map(n => ({ ...n, isRead: true }))
        );
        dispatch(updateUnreadCount(0));
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };
  
  useEffect(() => {
    // Fetch only when the panel becomes visible or role changes
    if (isVisible) {
      getNotifications();
    }
  }, [isVisible, role, getNotifications]);
  
  const handleNotificationClick = async (notification) => {
    // Debug: Log the notification object to see what fields are available
    console.log('Notification object:', notification);
    
    // Check for different possible ID field names
    const notificationId = notification.id || notification._id || notification.notificationId || notification.notification_id;
    
    if (!notificationId) {
      console.error('No valid notification ID found:', notification);
      console.error('Available fields:', Object.keys(notification));
      // Don't show alert - just log the error and continue with navigation
    } else {
      // Mark as read when clicked (if not already read)
      if (!notification.isRead) {
        try {
          await handleMarkAsRead(notificationId);
          // Scroll to viewed section so user sees where it moved
          if (viewedSectionRef.current) {
            try {
              viewedSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
            } catch (_) {}
          }
        } catch (error) {
          console.error('Failed to mark notification as read:', error);
          // Continue with navigation even if marking as read fails
        }
      }
    }

    // Give a short moment for the item to visually move into Viewed section
    // before closing the panel and navigating
    const delayCloseMs = 200;
    const closePanel = () => {
      if (onClose) onClose();
    };

    // Helper: extract offerId if present in notification payload
    const extractOfferId = (n) => {
      return (
        n?.offerId ||
        n?._offerId ||
        n?.offer_id ||
        n?.offer ||
        n?.data?.offerId ||
        n?.metadata?.offerId ||
        n?.payload?.offerId ||
        n?.resourceId ||
        n?.resource_id ||
        n?.entityId ||
        null
      );
    };

    // Offer entity notifications only (do NOT include generic events like redemption/payment)
    const isOfferRelated = (type) => {
      if (!type) return false;
      return [
        MERCHANT_NOTIFICATION_TYPES.NEW_OFFER_CREATED,
        MERCHANT_NOTIFICATION_TYPES.OFFER_APPROVED,
        MERCHANT_NOTIFICATION_TYPES.OFFER_REJECTED,
      ].includes(type) || String(type).toLowerCase().includes("offer");
    };

    const shouldOpenOfferModal = (n) => {
      const title = n?.title || n?.subject || n?.header || "";
      return isOfferRelated(n?.type) || /offer/i.test(title);
    };

    // Navigate based on notification type and role
    if (role === "merchant") {
      switch (notification.type) {
        case MERCHANT_NOTIFICATION_TYPES.OFFER_APPROVED:
        case MERCHANT_NOTIFICATION_TYPES.OFFER_REJECTED:
        case MERCHANT_NOTIFICATION_TYPES.NEW_REDEMPTION:
        case MERCHANT_NOTIFICATION_TYPES.PAYMENT_RECEIVED:
        case MERCHANT_NOTIFICATION_TYPES.LOW_STOCK:
        case MERCHANT_NOTIFICATION_TYPES.CAMPAIGN_END:
        case MERCHANT_NOTIFICATION_TYPES.NEW_CUSTOMER: {
          const offerId = shouldOpenOfferModal(notification)
            ? extractOfferId(notification)
            : null;
          const go = () => {
            if (offerId) {
              navigate("/merchant/offer-list", { state: { openOfferId: offerId } });
            } else {
              navigate("/merchant/offer-list");
            }
          };
          setTimeout(() => { closePanel(); go(); }, delayCloseMs);
          break;
        }
        case MERCHANT_NOTIFICATION_TYPES.SYSTEM_MAINTENANCE:
          navigate("/merchant/dashboard");
          break;
        default:
          navigate("/merchant/offer-list");
      }
    } else if (role === "fundraiser") {
      switch (notification.type) {
        case FUNDRAISER_NOTIFICATION_TYPES.REFERRAL_APPROVED:
        case FUNDRAISER_NOTIFICATION_TYPES.REFERRAL_REJECTED:
        case FUNDRAISER_NOTIFICATION_TYPES.NEW_REFERRAL:
        case FUNDRAISER_NOTIFICATION_TYPES.PAYMENT_RECEIVED:
        case FUNDRAISER_NOTIFICATION_TYPES.FUNDS_DEPOSITED:
        case FUNDRAISER_NOTIFICATION_TYPES.WITHDRAWAL_APPROVED:
        case FUNDRAISER_NOTIFICATION_TYPES.WITHDRAWAL_REJECTED:
        case FUNDRAISER_NOTIFICATION_TYPES.NEW_DONOR:
          setTimeout(() => { closePanel(); navigate("/fundraiser/referral-list"); }, delayCloseMs);
          break;
        case FUNDRAISER_NOTIFICATION_TYPES.CAMPAIGN_END:
        case FUNDRAISER_NOTIFICATION_TYPES.SYSTEM_MAINTENANCE:
          setTimeout(() => { closePanel(); navigate("/fundraiser/dashboard"); }, delayCloseMs);
          break;
        default:
          setTimeout(() => { closePanel(); navigate("/fundraiser/referral-list"); }, delayCloseMs);
      }
    } else {
      // Default (admin or others): deep-link only for actual offer notifications
      const offerId = shouldOpenOfferModal(notification) ? extractOfferId(notification) : null;
      const go = () => {
        if (offerId) {
          navigate("/offer-list", { state: { openOfferId: offerId } });
        } else {
          navigate("/offer-list");
        }
      };
      setTimeout(() => { closePanel(); go(); }, delayCloseMs);
    }
  };

  const formatNotificationTime = (timestamp) => {
    return moment(timestamp).fromNow();
  };

  const getNotificationIcon = (type) => {
    if (role === "merchant") {
      switch (type) {
        case MERCHANT_NOTIFICATION_TYPES.OFFER_APPROVED:
        case MERCHANT_NOTIFICATION_TYPES.PAYMENT_RECEIVED:
          return <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <ApprovedIcon className="w-4 h-4 text-green-600" />
          </div>;
        case MERCHANT_NOTIFICATION_TYPES.OFFER_REJECTED:
          return <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <RejectedIcon className="w-4 h-4 text-red-600" />
          </div>;
        case MERCHANT_NOTIFICATION_TYPES.NEW_OFFER_CREATED:
          return <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 text-xs font-medium">+</span>
          </div>;
        case MERCHANT_NOTIFICATION_TYPES.NEW_REDEMPTION:
          return <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 text-xs font-medium">R</span>
          </div>;
        case MERCHANT_NOTIFICATION_TYPES.LOW_STOCK:
          return <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
            <span className="text-yellow-600 text-xs font-medium">!</span>
          </div>;
        case MERCHANT_NOTIFICATION_TYPES.CAMPAIGN_END:
          return <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
            <span className="text-orange-600 text-xs font-medium">T</span>
          </div>;
        case MERCHANT_NOTIFICATION_TYPES.SYSTEM_MAINTENANCE:
          return <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
            <span className="text-orange-600 text-xs">⚙</span>
          </div>;
        case MERCHANT_NOTIFICATION_TYPES.NEW_CUSTOMER:
          return <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
            <span className="text-purple-600 text-xs">👤</span>
          </div>;
        default:
          return <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
            <span className="text-gray-600 text-xs font-medium">N</span>
          </div>;
      }
    } else if (role === "fundraiser") {
      switch (type) {
        case FUNDRAISER_NOTIFICATION_TYPES.REFERRAL_APPROVED:
        case FUNDRAISER_NOTIFICATION_TYPES.PAYMENT_RECEIVED:
        case FUNDRAISER_NOTIFICATION_TYPES.FUNDS_DEPOSITED:
        case FUNDRAISER_NOTIFICATION_TYPES.WITHDRAWAL_APPROVED:
          return <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <ApprovedIcon className="w-4 h-4 text-green-600" />
          </div>;
        case FUNDRAISER_NOTIFICATION_TYPES.REFERRAL_REJECTED:
        case FUNDRAISER_NOTIFICATION_TYPES.WITHDRAWAL_REJECTED:
          return <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
            <RejectedIcon className="w-4 h-4 text-red-600" />
          </div>;
        case FUNDRAISER_NOTIFICATION_TYPES.NEW_REFERRAL:
        case FUNDRAISER_NOTIFICATION_TYPES.NEW_DONOR:
          return <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-blue-600 text-xs font-medium">R</span>
          </div>;
        case FUNDRAISER_NOTIFICATION_TYPES.CAMPAIGN_END:
          return <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
            <span className="text-yellow-600 text-xs font-medium">T</span>
          </div>;
        case FUNDRAISER_NOTIFICATION_TYPES.SYSTEM_MAINTENANCE:
          return <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
            <span className="text-orange-600 text-xs">⚙</span>
          </div>;
        default:
          return <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
            <span className="text-gray-600 text-xs font-medium">N</span>
          </div>;
      }
    } else {
      return <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
        <span className="text-gray-600 text-xs font-medium">N</span>
      </div>;
    }
  };

  const getNotificationTitle = (type) => {
    if (role === "merchant") {
      switch (type) {
        case MERCHANT_NOTIFICATION_TYPES.OFFER_APPROVED:
          return "Offer Approved";
        case MERCHANT_NOTIFICATION_TYPES.OFFER_REJECTED:
          return "Offer Rejected";
        case MERCHANT_NOTIFICATION_TYPES.NEW_OFFER_CREATED:
          return "New Offer Created";
        case MERCHANT_NOTIFICATION_TYPES.NEW_REDEMPTION:
          return "New Redemption";
        case MERCHANT_NOTIFICATION_TYPES.PAYMENT_RECEIVED:
          return "Payment Received";
        case MERCHANT_NOTIFICATION_TYPES.LOW_STOCK:
          return "Low Stock Alert";
        case MERCHANT_NOTIFICATION_TYPES.CAMPAIGN_END:
          return "Campaign Ending Soon";
        case MERCHANT_NOTIFICATION_TYPES.SYSTEM_MAINTENANCE:
          return "System Maintenance";
        case MERCHANT_NOTIFICATION_TYPES.NEW_CUSTOMER:
          return "New Customer";
        default:
          return "Merchant Activity";
      }
    } else if (role === "fundraiser") {
      switch (type) {
        case FUNDRAISER_NOTIFICATION_TYPES.REFERRAL_APPROVED:
          return "Referral Approved";
        case FUNDRAISER_NOTIFICATION_TYPES.REFERRAL_REJECTED:
          return "Referral Rejected";
        case FUNDRAISER_NOTIFICATION_TYPES.NEW_REFERRAL:
          return "New Referral";
        case FUNDRAISER_NOTIFICATION_TYPES.PAYMENT_RECEIVED:
          return "Payment Received";
        case FUNDRAISER_NOTIFICATION_TYPES.FUNDS_DEPOSITED:
          return "Funds Deposited";
        case FUNDRAISER_NOTIFICATION_TYPES.WITHDRAWAL_APPROVED:
          return "Withdrawal Approved";
        case FUNDRAISER_NOTIFICATION_TYPES.WITHDRAWAL_REJECTED:
          return "Withdrawal Rejected";
        case FUNDRAISER_NOTIFICATION_TYPES.CAMPAIGN_END:
          return "Campaign Ending Soon";
        case FUNDRAISER_NOTIFICATION_TYPES.SYSTEM_MAINTENANCE:
          return "System Maintenance";
        case FUNDRAISER_NOTIFICATION_TYPES.NEW_DONOR:
          return "New Donor";
        default:
          return "Fundraiser Activity";
      }
    } else {
      return "Notification";
    }
  };

  // Prefer backend-provided title fields when available
  const getDisplayTitle = (notification) => {
    return (
      notification?.title ||
      notification?.subject ||
      notification?.header ||
      getNotificationTitle(notification?.type)
    );
  };

  // Prefer message from backend, else build a friendly default
  const getDisplayMessage = (notification) => {
    if (notification?.message || notification?.body) {
      return notification.message || notification.body;
    }
    return formatNotificationMessage(
      notification?.type,
      notification?.data || {},
      role || "merchant"
    );
  };

  // Border color mapping not needed for the simplified Figma-aligned design

  // Optional keyboard shortcut for refresh (Ctrl/Cmd + R) when panel is open
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (!isVisible) return;
      if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
        event.preventDefault();
        getNotifications();
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isVisible, getNotifications]);

  return (
    <div
      className={`fixed top-[5.5rem] right-0 h-[88vh] w-[92vw] sm:w-[360px] max-w-[360px] z-50 bg-white flex flex-col overflow-scroll shadow-[0px_8px_24px_rgba(0,0,0,0.18)] transition-all duration-300 ease-in-out ${slideInClass}`}
    >
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-800">Notifications</h3>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button onClick={handleMarkAllAsRead} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
              Mark all as read
            </button>
          )}
          <button onClick={getNotifications} title="Refresh" className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded-full">
            ↻
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading || storeNotifications === null ? (
          <Loader />
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-gray-500">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mb-3">
              <span className="text-gray-400 text-xl">🔔</span>
            </div>
            <p className="text-sm">No notifications yet</p>
            <p className="text-xs text-gray-400 mt-1">We'll notify you when something important happens</p>
          </div>
        ) : (
          <div className="p-2">
            {(() => {
              const normalizeTimestamp = (n) => {
                const ts = n?.createdAt || n?.created_at || n?.timestamp || n?.time || n?.date;
                const d = ts ? new Date(ts) : null;
                return d && !isNaN(d.getTime()) ? d.getTime() : 0;
              };
              const sorted = [...notifications].sort((a, b) => normalizeTimestamp(b) - normalizeTimestamp(a));
              return (
                <>
                  {sorted.map((notification, index) => {
                    const notificationId = notification.id || notification._id || notification.notificationId || notification.notification_id;
                    return (
                      <div
                        key={notificationId || `notif-${index}`}
                        onClick={() => handleNotificationClick(notification)}
                        className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all duration-200 mb-2 border ${
                          !notification.isRead
                            ? "bg-white hover:bg-blue-50 border-blue-100"
                            : "bg-white hover:bg-gray-50 border-gray-100"
                        }`}
                      >
                        <div className="flex-shrink-0 mt-1">{getNotificationIcon(notification.type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-semibold ${!notification.isRead ? "text-gray-900" : "text-gray-700"}`}>
                                {getDisplayTitle(notification)}
                              </p>
                              {!notification.isRead && <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 shadow-sm"></div>}
                            </div>
                            <span className="text-xs text-gray-500 flex-shrink-0 ml-2">{formatNotificationTime(notification.createdAt)}</span>
                          </div>
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{getDisplayMessage(notification)}</p>
                        </div>
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;